import { Container, Graphics, Rectangle, RenderTexture, Texture, type Renderer } from 'pixi.js';
import {
  ATLAS_CELL_COUNT, BULLET_LANES, BULLET_SHAPES, KIND_GEM, KIND_SPARK,
  type BulletLane, type BulletShape,
} from '../sim/bullets/bulletKinds';

export const CELL = 64;
const HALF = CELL / 2;

const LANE_COLOR: Record<BulletLane, number> = {
  pink: 0xff2d78,
  orange: 0xff9a3d,
  cyan: 0x3df2ff,
};

/**
 * Bakes every bullet/particle style into ONE RenderTexture atlas at boot:
 * white core + colored rim + baked radial glow (the Touhou readability trick,
 * with zero runtime filter cost). All frames share one texture source, so a
 * ParticleContainer of bullets is a single draw call.
 */
export class TextureFactory {
  frames: Texture[] = [];

  build(renderer: Renderer): void {
    const container = new Container();
    for (let s = 0; s < BULLET_SHAPES.length; s++) {
      for (let l = 0; l < BULLET_LANES.length; l++) {
        const g = drawShape(BULLET_SHAPES[s]!, LANE_COLOR[BULLET_LANES[l]!]);
        g.position.set((s * BULLET_LANES.length + l) * CELL + HALF, HALF);
        container.addChild(g);
      }
    }
    const spark = drawSpark();
    spark.position.set(KIND_SPARK * CELL + HALF, HALF);
    container.addChild(spark);
    const gem = drawGem();
    gem.position.set(KIND_GEM * CELL + HALF, HALF);
    container.addChild(gem);

    const rt = RenderTexture.create({
      width: ATLAS_CELL_COUNT * CELL,
      height: CELL,
      antialias: true,
      resolution: renderer.resolution,
    });
    renderer.render({ container, target: rt, clear: true });

    this.frames = [];
    for (let i = 0; i < ATLAS_CELL_COUNT; i++) {
      this.frames.push(new Texture({
        source: rt.source,
        frame: new Rectangle(i * CELL, 0, CELL, CELL),
      }));
    }
  }
}

function glowLayers(g: Graphics, color: number, maxR: number, layers = 5): void {
  for (let i = 0; i < layers; i++) {
    const r = maxR - (i * (maxR * 0.55)) / layers;
    g.circle(0, 0, r).fill({ color, alpha: 0.035 + i * 0.02 });
  }
}

function drawShape(shape: BulletShape, color: number): Graphics {
  const g = new Graphics();
  switch (shape) {
    case 'orb':
      glowLayers(g, color, 26);
      g.circle(0, 0, 11).fill({ color, alpha: 0.95 });
      g.circle(0, 0, 6).fill(0xffffff);
      break;
    case 'shard': {
      glowLayers(g, color, 24);
      g.poly([-14, 0, -3, -6, 15, 0, -3, 6]).fill({ color, alpha: 0.95 });
      g.poly([-6, 0, 0, -2.6, 9, 0, 0, 2.6]).fill(0xffffff);
      break;
    }
    case 'ring':
      glowLayers(g, color, 26);
      g.circle(0, 0, 12).stroke({ width: 5, color, alpha: 0.95 });
      g.circle(0, 0, 12).stroke({ width: 1.8, color: 0xffffff });
      break;
    case 'diamond':
      glowLayers(g, color, 25);
      g.poly([0, -12, 12, 0, 0, 12, -12, 0]).fill({ color, alpha: 0.95 });
      g.poly([0, -6, 6, 0, 0, 6, -6, 0]).fill(0xffffff);
      break;
  }
  return g;
}

function drawSpark(): Graphics {
  const g = new Graphics();
  glowLayers(g, 0xffffff, 10, 3);
  g.circle(0, 0, 3).fill(0xffffff);
  return g;
}

function drawGem(): Graphics {
  const g = new Graphics();
  const color = 0x59f28b;
  glowLayers(g, color, 16, 3);
  g.poly([0, -9, 7, -3, 4, 8, -4, 8, -7, -3]).fill({ color, alpha: 0.95 });
  g.poly([0, -5, 3.5, -1.5, 2, 4, -2, 4, -3.5, -1.5]).fill({ color: 0xd9ffe9 });
  return g;
}
